import { useEffect, useReducer } from 'react';

import { useLockBodyScroll } from '@/shared/hooks';

import {
  AI_BUILDER_SHELL_CLOSE_TRANSITION_DURATION_MS,
  AI_BUILDER_SHELL_TRANSITION_DURATION_MS,
  type AiBuilderPageSurfaceMode,
  type AiBuilderPageSurfaceState,
  type AiBuilderShellTransitionState,
} from '../components/AiBuilderShellTransition';

type AiBuilderPanelTransitionState =
  | 'closed'
  | 'opening-shell'
  | 'open'
  | 'closing-shell';

type AiBuilderShellTransitionEvent =
  | 'OPEN_REQUESTED'
  | 'CLOSE_REQUESTED'
  | 'MORPH_COMPLETED'
  | 'CLOSE_COMPLETED';

export type AiBuilderShellTransitionResult = {
  pageSurfaceState: AiBuilderPageSurfaceState;
  pageSurfaceMode: AiBuilderPageSurfaceMode;
  chatPanelTransitionState: AiBuilderShellTransitionState | null;
};

export const useAiBuilderShellTransition = ({
  shouldOpen,
}: {
  shouldOpen: boolean;
}): AiBuilderShellTransitionResult => {
  const [transitionState, dispatch] = useReducer(
    aiBuilderShellTransitionReducer,
    'closed',
  );

  useEffect(() => {
    dispatch(shouldOpen ? 'OPEN_REQUESTED' : 'CLOSE_REQUESTED');
  }, [shouldOpen]);

  useEffect(() => {
    if (transitionState === 'opening-shell') {
      const openTimer = window.setTimeout(() => {
        dispatch('MORPH_COMPLETED');
      }, AI_BUILDER_SHELL_TRANSITION_DURATION_MS);

      return () => {
        window.clearTimeout(openTimer);
      };
    }

    if (transitionState !== 'closing-shell') return;

    const closeTimer = window.setTimeout(() => {
      dispatch('CLOSE_COMPLETED');
    }, AI_BUILDER_SHELL_CLOSE_TRANSITION_DURATION_MS);

    return () => {
      window.clearTimeout(closeTimer);
    };
  }, [transitionState]);

  useLockBodyScroll(transitionState !== 'closed');

  return {
    pageSurfaceState: toAiBuilderPageSurfaceState(transitionState),
    pageSurfaceMode: toAiBuilderPageSurfaceMode(transitionState),
    chatPanelTransitionState:
      toAiBuilderChatPanelTransitionState(transitionState),
  };
};

const aiBuilderShellTransitionReducer = (
  state: AiBuilderPanelTransitionState,
  event: AiBuilderShellTransitionEvent,
): AiBuilderPanelTransitionState => {
  switch (state) {
    case 'closed':
      return event === 'OPEN_REQUESTED' ? 'opening-shell' : state;
    case 'opening-shell':
      if (event === 'CLOSE_REQUESTED') return 'closed';
      if (event === 'MORPH_COMPLETED') return 'open';
      return state;
    case 'open':
      return event === 'CLOSE_REQUESTED' ? 'closing-shell' : state;
    case 'closing-shell':
      if (event === 'OPEN_REQUESTED') return 'opening-shell';
      if (event === 'CLOSE_COMPLETED') return 'closed';
      return state;
  }
};

const toAiBuilderPageSurfaceState = (
  transitionState: AiBuilderPanelTransitionState,
): AiBuilderPageSurfaceState => {
  if (transitionState === 'closed') return 'page';
  if (transitionState === 'opening-shell') return 'morphing';
  if (transitionState === 'closing-shell') return 'closing';
  return 'shell';
};

const toAiBuilderPageSurfaceMode = (
  transitionState: AiBuilderPanelTransitionState,
): AiBuilderPageSurfaceMode => {
  if (transitionState === 'closed') return 'page';
  if (transitionState === 'opening-shell') return 'ai-shell-entering';
  if (transitionState === 'closing-shell') return 'page-restoring';
  return 'ai-shell';
};

const toAiBuilderChatPanelTransitionState = (
  transitionState: AiBuilderPanelTransitionState,
): AiBuilderShellTransitionState | null => {
  if (transitionState === 'opening-shell') return 'opening';
  if (transitionState === 'open') return 'open';
  if (transitionState === 'closing-shell') return 'closing';
  return null;
};
